import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import StarRatingBar from 'components/StarRatingBar/StarRatingBar';
import { facilityService, FacilityReview, ReviewStatistics } from 'services/facilityService';
import {
  Body,
  Header,
  BackButton,
  StarWrapper,
  AverageStar,
  StarContainer,
  StarTitle,
  ScoreContainer,
  CheckBox,
  Review1,
  ReviewDate,
  ReviewInfo,
  StarRateWrap2,
  ImageContainer,
  ReviewContainer,
  ReviewDetail,
  ImageGrid,
  ImageWrapper,
  ImageOverlay,
  ImageModal,
  ModalContent,
  ModalImage,
  CloseButton,
  NavButton,
  ImageCounter
} from './Review.styles';

function Review() {
  const [showImages, setShowImages] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<FacilityReview[]>([]);
  const [averageScore, setAverageScore] = useState<number | null>(null);
  const [starCounts, setStarCounts] = useState<ReviewStatistics['starRatings'] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentImages, setCurrentImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  const calcStarRates = (score: number): number[] => {
    let tempStarRatesArr = [0, 0, 0, 0, 0];
    let starVerScore = (score * 20 * 70) / 100;
    let idx = 0;
    while (starVerScore > 14) {
      tempStarRatesArr[idx] = 14;
      idx += 1;
      starVerScore -= 14;
    }
    tempStarRatesArr[idx] = starVerScore;
    return tempStarRatesArr;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch review statistics
        const statistics = await facilityService.getReviewStatistics(Number(id));
        setAverageScore(statistics.averageStarRating ?? 0);
        setStarCounts(statistics.starRatings);

        // Fetch reviews
        const response = await facilityService.getFacilityReviews(Number(id), 0, 10, 'createdAt,DESC');
        setReviews(response.content);
        setHasMore(!response.last);
        setPage(0);
      } catch (error) {
        console.error('데이터 조회 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const loadMoreReviews = useCallback(async () => {
    if (!id || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const response = await facilityService.getFacilityReviews(Number(id), nextPage, 10, 'createdAt,DESC');
      setReviews(prev => [...prev, ...response.content]);
      setHasMore(!response.last);
      setPage(nextPage);
    } catch (error) {
      console.error('추가 리뷰 조회 실패:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [id, loadingMore, hasMore, page]);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      const scrollPosition = window.scrollY + window.innerHeight;
      const pageHeight = document.documentElement.scrollHeight;

      if (scrollPosition >= pageHeight - 100) {
        loadMoreReviews();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore, loadMoreReviews]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  const openImageModal = (images: string[], index: number) => {
    setCurrentImages(images);
    setCurrentImageIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeImageModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const goToPrevImage = useCallback(() => {
    setCurrentImageIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNextImage = useCallback(() => {
    setCurrentImageIndex((prev) => Math.min(currentImages.length - 1, prev + 1));
  }, [currentImages.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!modalOpen) return;
      if (e.key === 'Escape') closeImageModal();
      if (e.key === 'ArrowLeft') goToPrevImage();
      if (e.key === 'ArrowRight') goToNextImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, closeImageModal, goToPrevImage, goToNextImage]);

  if (loading) {
    return (
      <Body>
        <Header>
          <BackButton onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          리뷰
        </Header>
        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
          로딩 중...
        </div>
      </Body>
    );
  }

  const totalReviewCount = starCounts
    ? Object.values(starCounts).reduce((sum, count) => sum + count, 0)
    : 0;

  return (
    <Body>
      <Header>
        <BackButton onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </BackButton>
        리뷰 {totalReviewCount}개
      </Header>
      <StarWrapper>
        <StarContainer>
          <StarTitle>평균 {averageScore !== null ? averageScore.toFixed(1) : '평점 없음'}</StarTitle>
          <AverageStar>
            {averageScore !== null ? (
              <>
                {calcStarRates(averageScore).map((rate, idx) => (
                  <span className="star_icon" key={`avg_star_${idx}`}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 14 13"
                      fill="#cacaca"
                    >
                      <clipPath id={`avg_starClip_${idx}`}>
                        <rect width={`${rate}`} height="16" />
                      </clipPath>
                      <path
                        id={`avg_star_${idx}`}
                        d="M9,2l2.163,4.279L16,6.969,12.5,10.3l.826,4.7L9,12.779,4.674,15,5.5,10.3,2,6.969l4.837-.69Z"
                        transform="translate(-2 -2)"
                      />
                      <use
                        clipPath={`url(#avg_starClip_${idx})`}
                        href={`#avg_star_${idx}`}
                        fill="#FFBE55"
                      />
                    </svg>
                  </span>
                ))}
              </>
            ) : (
              '평점 없음'
            )}
          </AverageStar>
        </StarContainer>
        <ScoreContainer>
          <StarRatingBar starCounts={starCounts ?? undefined} />
        </ScoreContainer>
      </StarWrapper>
      <CheckBox>
        <input
          type="checkbox"
          checked={showImages}
          onChange={() => setShowImages(!showImages)}
        />
        <div>포토리뷰</div>
        <select className="sorting">
          <option>최신순</option>
        </select>
      </CheckBox>
      <ReviewContainer>
        {reviews.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            작성된 리뷰가 없습니다.
          </div>
        ) : (
          <>
            {reviews.filter((review) => !showImages || (review.images && review.images.length > 0)).length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                포토 리뷰가 없습니다.
              </div>
            ) : (
              reviews
                .filter((review) => !showImages || (review.images && review.images.length > 0))
                .map((review) => {
                const ratesResArr = calcStarRates(review.startRating);
                return (
                  <Review1 key={review.id}>
                    <ImageContainer>
                      <img
                        className="img1"
                        src={review.member.profileImageUrl || 'https://via.placeholder.com/40'}
                        alt={`프로필 이미지`}
                        referrerPolicy="no-referrer"
                      />
                      <ReviewInfo>
                        <div className="nickName">{review.member.name}</div>
                        <ReviewDate>{formatDate(review.createdAt)}</ReviewDate>
                      </ReviewInfo>
                      <StarRateWrap2>
                        {ratesResArr.map((rate, idx) => (
                          <span className="star_icon" key={`${review.id}_${idx}`}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="11"
                              height="14"
                              viewBox="0 0 14 13"
                              fill="#cacaca"
                            >
                              <clipPath id={`starClip_${review.id}_${idx}`}>
                                <rect width={`${rate}`} height="14" />
                              </clipPath>
                              <path
                                id={`star_${review.id}_${idx}`}
                                d="M9,2l2.163,4.279L16,6.969,12.5,10.3l.826,4.7L9,12.779,4.674,15,5.5,10.3,2,6.969l4.837-.69Z"
                                transform="translate(-2 -2)"
                              />
                              <use
                                clipPath={`url(#starClip_${review.id}_${idx})`}
                                href={`#star_${review.id}_${idx}`}
                                fill='#FFBE55'
                              />
                            </svg>
                          </span>
                        ))}
                      </StarRateWrap2>
                    </ImageContainer>
                    <ReviewDetail>{review.content}</ReviewDetail>
                    {review.images && review.images.length > 0 && (
                      <ImageGrid>
                        {review.images.slice(0, 4).map((imageUrl, imgIndex) => {
                          const isLastImage = imgIndex === 3 && review.images.length > 4;
                          const remainingCount = review.images.length - 4;

                          return (
                            <ImageWrapper
                              key={imgIndex}
                              hasOverlay={isLastImage}
                              onClick={() => openImageModal(review.images, imgIndex)}
                              style={{ cursor: 'pointer' }}
                            >
                              {isLastImage && <ImageOverlay>{remainingCount} +</ImageOverlay>}
                              <img
                                src={imageUrl}
                                alt={`리뷰${review.id}-${imgIndex + 1}`}
                                referrerPolicy="no-referrer"
                              />
                            </ImageWrapper>
                          );
                        })}
                      </ImageGrid>
                    )}
                  </Review1>
                );
              })
            )}
            {loadingMore && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                로딩 중...
              </div>
            )}
            {!hasMore && reviews.length > 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                모든 리뷰를 불러왔습니다.
              </div>
            )}
          </>
        )}
      </ReviewContainer>

      {modalOpen && (
        <ImageModal onClick={closeImageModal}>
          <CloseButton onClick={closeImageModal}>×</CloseButton>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <NavButton
              direction="prev"
              onClick={goToPrevImage}
              disabled={currentImageIndex === 0}
            >
              ‹
            </NavButton>
            <ModalImage
              src={currentImages[currentImageIndex]}
              alt={`Image ${currentImageIndex + 1}`}
              referrerPolicy="no-referrer"
            />
            <NavButton
              direction="next"
              onClick={goToNextImage}
              disabled={currentImageIndex === currentImages.length - 1}
            >
              ›
            </NavButton>
            <ImageCounter>
              {currentImageIndex + 1} / {currentImages.length}
            </ImageCounter>
          </ModalContent>
        </ImageModal>
      )}
    </Body>
  );
}

export default Review;
